import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Menu,
  X,
  Home,
  BookOpen,
  Newspaper,
  MessageCircle,
  Calendar,
  Phone,
  Info,
  Briefcase,
  LogOut,
  ChevronDown,
  User,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { publicNavItems, getDashboardPath, getDisplayRole } from '../../config/navigation';
import { Avatar } from '../ui/Avatar';

// ─── Icon map ────────────────────────────────────────────────────────────────
const iconMap: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  Home,
  BookOpen,
  Newspaper,
  MessageCircle,
  Calendar,
  Phone,
  Info,
  Briefcase,
  LogOut,
  User,
};

// ─── Component ───────────────────────────────────────────────────────────────
export const PublicHeader: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

  const { user, isAuthenticated, isLoading, logoutUser } = useAuth();
  const location = useLocation();

  const userMenuRef = useRef<HTMLDivElement>(null);
  const userMenuButtonRef = useRef<HTMLButtonElement>(null);
  const dropdownItemsRef = useRef<(HTMLElement | null)[]>([]);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // ── Derived ──────────────────────────────────────────────────────────────
  const dashboardPath =
    user && isAuthenticated
      ? getDashboardPath(user.role as 'student' | 'mentor', user.isAdmin)
      : '/dashboard/student';

  const displayRole = user ? getDisplayRole(user.role) : 'Guest';

  // ── Handlers ─────────────────────────────────────────────────────────────
  const closeAll = useCallback(() => {
    setIsMobileMenuOpen(false);
    setIsUserDropdownOpen(false);
  }, []);

  const handleLogout = useCallback(async () => {
    closeAll();
    await logoutUser();
  }, [logoutUser, closeAll]);

  const toggleMobileMenu = useCallback(() => {
    setIsUserDropdownOpen(false);
    setIsMobileMenuOpen((prev) => !prev);
  }, []);

  const toggleUserDropdown = useCallback(() => {
    setIsMobileMenuOpen(false);
    setIsUserDropdownOpen((prev) => !prev);
  }, []);

  // ── Click-outside: close user dropdown ───────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(e.target as Node)
      ) {
        setIsUserDropdownOpen(false);
      }
    };
    if (isUserDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isUserDropdownOpen]);

  // ── Click-outside: close mobile menu ─────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(e.target as Node)
      ) {
        setIsMobileMenuOpen(false);
      }
    };
    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  // ── Escape key ───────────────────────────────────────────────────────────
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isUserDropdownOpen) {
          setIsUserDropdownOpen(false);
          userMenuButtonRef.current?.focus();
        } else if (isMobileMenuOpen) {
          setIsMobileMenuOpen(false);
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isUserDropdownOpen, isMobileMenuOpen]);

  // ── Keyboard navigation in dropdown ──────────────────────────────────────
  const handleDropdownKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isUserDropdownOpen) return;

      const items = dropdownItemsRef.current.filter(Boolean) as HTMLElement[];
      const currentIndex = items.indexOf(document.activeElement as HTMLElement);

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        items[next]?.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        items[prev]?.focus();
      } else if (e.key === 'Tab') {
        // Let Tab close the dropdown naturally
        setIsUserDropdownOpen(false);
      }
    },
    [isUserDropdownOpen]
  );

  // ── Focus first item when dropdown opens ─────────────────────────────────
  useEffect(() => {
    if (isUserDropdownOpen) {
      requestAnimationFrame(() => {
        const items = dropdownItemsRef.current.filter(Boolean) as HTMLElement[];
        items[0]?.focus();
      });
    }
  }, [isUserDropdownOpen]);

  // ── Prevent body scroll when mobile menu is open ─────────────────────────
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // ── Close mobile menu on route change ────────────────────────────────────
  useEffect(() => {
    closeAll();
  }, [location.pathname, closeAll]);

  // ── Nav link active check ────────────────────────────────────────────────
  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // ── Loading skeleton ─────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <header className="sticky top-0 z-50 bg-white border-b border-border-subtle">
        <div className="container-main">
          <div className="flex h-[72px] items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-bg-tertiary animate-pulse rounded-lg" />
              <div className="hidden sm:block space-y-1.5">
                <div className="h-4 w-28 bg-bg-tertiary animate-pulse rounded" />
                <div className="h-3 w-20 bg-bg-tertiary animate-pulse rounded" />
              </div>
            </div>
            <div className="hidden xl:flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-4 w-16 bg-bg-tertiary animate-pulse rounded" />
              ))}
            </div>
            <div className="flex items-center gap-3">
              <div className="h-9 w-20 bg-bg-tertiary animate-pulse rounded-lg" />
              <div className="h-9 w-24 bg-bg-tertiary animate-pulse rounded-lg" />
            </div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <>
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-border-subtle">
        <div className="container-main">
          <div className="flex h-[72px] items-center justify-between gap-4">
            {/* ── Brand ─────────────────────────────────────────────── */}
            <Link
              to="/"
              className="flex items-center gap-3 shrink-0 group"
              aria-label="ShreeGuruCool Technologies — Home"
            >
              <img
                src="/logo.jpeg"
                alt=""
                className="h-10 w-10 object-contain rounded-lg shadow-sm group-hover:shadow-md transition-shadow duration-200"
                aria-hidden="true"
              />
              <div className="hidden sm:flex flex-col leading-tight">
                <span className="text-[1.125rem] font-bold text-primary-700 tracking-tight">
                  ShreeGuruCool
                </span>
                <span className="text-[0.6875rem] font-medium text-text-muted tracking-[0.15em] uppercase">
                  TeKnologies
                </span>
              </div>
            </Link>

            {/* ── Desktop Navigation ────────────────────────────────── */}
            <nav
              className="hidden xl:flex items-center gap-1"
              aria-label="Main navigation"
            >
              {publicNavItems.map((item) => {
                const Icon = iconMap[item.icon];
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`
                      relative flex items-center gap-1.5 px-3 py-2 rounded-md text-[0.8125rem] font-medium
                      transition-colors duration-150
                      ${
                        active
                          ? 'text-primary-600 bg-primary-50'
                          : 'text-text-secondary hover:text-text-primary hover:bg-bg-tertiary'
                      }
                    `}
                    aria-current={active ? 'page' : undefined}
                  >
                    {Icon && (
                      <Icon
                        size={16}
                        className={`shrink-0 ${active ? 'text-primary-500' : 'text-text-muted'}`}
                      />
                    )}
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </nav>

            {/* ── Right side: Auth actions ───────────────────────────── */}
            <div className="flex items-center gap-2">
              {isAuthenticated && user ? (
                <>
                  {/* Dashboard link (desktop only) */}
                  <Link
                    to={dashboardPath}
                    className="hidden lg:flex items-center gap-1.5 px-3 py-2 rounded-md text-[0.8125rem] font-medium text-text-secondary hover:text-primary-600 hover:bg-primary-50 transition-colors duration-150"
                  >
                    <Briefcase size={16} className="text-text-muted" />
                    <span>Dashboard</span>
                  </Link>

                  {/* User menu */}
                  <div className="relative" ref={userMenuRef}>
                    <button
                      ref={userMenuButtonRef}
                      onClick={toggleUserDropdown}
                      className={`
                        flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm
                        transition-colors duration-150
                        ${
                          isUserDropdownOpen
                            ? 'bg-bg-tertiary'
                            : 'hover:bg-bg-tertiary'
                        }
                      `}
                      aria-label="User menu"
                      aria-expanded={isUserDropdownOpen}
                      aria-haspopup="true"
                    >
                      <Avatar
                        src={user.avatar}
                        name={user.name}
                        size="sm"
                      />
                      <span className="hidden md:block text-[0.8125rem] font-medium text-text-primary max-w-[160px] truncate">
                        {user.name}
                      </span>
                      <ChevronDown
                        size={14}
                        className={`hidden md:block text-text-muted transition-transform duration-200 ${
                          isUserDropdownOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    {/* User dropdown */}
                    {isUserDropdownOpen && (
                      <div
                        className="absolute right-0 mt-2 w-56 bg-white border border-border-default rounded-xl shadow-lg z-50 py-1 animate-scale-in"
                        role="menu"
                        aria-label="User menu"
                        onKeyDown={handleDropdownKeyDown}
                      >
                        {/* User info header */}
                        <div className="px-4 py-3 border-b border-border-subtle">
                          <p className="text-sm font-semibold text-text-primary truncate">
                            {user.name}
                          </p>
                          <p className="text-xs text-text-secondary capitalize mt-0.5">
                            {displayRole}
                          </p>
                          {user.isAdmin && (
                            <span className="inline-block mt-1.5 px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wider bg-primary-50 text-primary-700 rounded-full">
                              Admin
                            </span>
                          )}
                        </div>

                        {/* Menu items */}
                        <div className="py-1">
                          <Link
                            ref={(el) => { dropdownItemsRef.current[0] = el; }}
                            to={dashboardPath}
                            onClick={() => setIsUserDropdownOpen(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-colors outline-none focus-visible:bg-bg-tertiary"
                            role="menuitem"
                          >
                            <Briefcase size={16} className="text-text-muted shrink-0" />
                            Dashboard
                          </Link>
                          <Link
                            ref={(el) => { dropdownItemsRef.current[1] = el; }}
                            to="/dashboard/student/profile"
                            onClick={() => setIsUserDropdownOpen(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-colors outline-none focus-visible:bg-bg-tertiary"
                            role="menuitem"
                          >
                            <User size={16} className="text-text-muted shrink-0" />
                            Profile
                          </Link>
                        </div>

                        {/* Separator */}
                        <div className="h-px bg-border-subtle mx-2" role="separator" />

                        {/* Logout */}
                        <div className="py-1">
                          <button
                            ref={(el) => { dropdownItemsRef.current[2] = el; }}
                            onClick={handleLogout}
                            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-danger hover:bg-danger-bg transition-colors outline-none focus-visible:bg-danger-bg"
                            role="menuitem"
                          >
                            <LogOut size={16} className="shrink-0" />
                            Logout
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                /* Logged-out actions */
                <div className="hidden sm:flex items-center gap-2">
                  <Link
                    to="/login"
                    className="px-4 py-2 rounded-lg text-sm font-medium text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-colors duration-150"
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors duration-150 shadow-sm"
                  >
                    Get Started
                  </Link>
                </div>
              )}

              {/* ── Mobile menu button ────────────────────────────── */}
              <button
                onClick={toggleMobileMenu}
                className="xl:hidden p-2 rounded-lg text-text-secondary hover:bg-bg-tertiary hover:text-text-primary transition-colors duration-150"
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-menu"
              >
                {isMobileMenuOpen ? (
                  <X size={22} />
                ) : (
                  <Menu size={22} />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ── Mobile Menu Overlay + Panel ──────────────────────────────── */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[2px] xl:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />

          {/* Panel */}
          <div
            id="mobile-menu"
            ref={mobileMenuRef}
            className="fixed top-[72px] left-0 right-0 z-50 xl:hidden bg-white border-b border-border-subtle shadow-lg animate-slide-down overflow-y-auto"
            style={{ maxHeight: 'calc(100dvh - 72px)' }}
            role="dialog"
            aria-label="Mobile navigation"
          >
            {/* Navigation links */}
            <nav className="px-4 py-3 space-y-0.5" aria-label="Mobile navigation">
              {publicNavItems.map((item) => {
                const Icon = iconMap[item.icon];
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`
                      flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium
                      transition-colors duration-150
                      ${
                        active
                          ? 'text-primary-600 bg-primary-50'
                          : 'text-text-secondary hover:text-text-primary hover:bg-bg-tertiary'
                      }
                    `}
                    aria-current={active ? 'page' : undefined}
                  >
                    {Icon && (
                      <Icon
                        size={18}
                        className={`shrink-0 ${active ? 'text-primary-500' : 'text-text-muted'}`}
                      />
                    )}
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </nav>

            {/* Auth section */}
            <div className="px-4 py-4 border-t border-border-subtle">
              {!isAuthenticated || !user ? (
                <div className="flex flex-col gap-2">
                  <Link
                    to="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-medium text-text-primary bg-bg-tertiary hover:bg-border-subtle transition-colors"
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors shadow-sm"
                  >
                    Get Started
                  </Link>
                </div>
              ) : (
                <div className="space-y-1">
                  {/* Mobile user info */}
                  <div className="flex items-center gap-3 px-3 py-2 mb-2">
                    <Avatar
                      src={user.avatar}
                      name={user.name}
                      size="md"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-text-primary truncate">
                        {user.name}
                      </p>
                      <p className="text-xs text-text-secondary capitalize">
                        {displayRole}
                        {user.isAdmin && (
                          <span className="ml-1.5 inline-block px-1.5 py-0.5 text-[0.5625rem] font-semibold uppercase bg-primary-50 text-primary-700 rounded-full">
                            Admin
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  <Link
                    to={dashboardPath}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-colors"
                  >
                    <Briefcase size={18} className="text-text-muted shrink-0" />
                    Dashboard
                  </Link>
                  <Link
                    to="/dashboard/student/profile"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-colors"
                  >
                    <User size={18} className="text-text-muted shrink-0" />
                    Profile
                  </Link>

                  <div className="h-px bg-border-subtle my-1" />

                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full px-3 py-3 rounded-lg text-sm font-medium text-danger hover:bg-danger-bg transition-colors"
                  >
                    <LogOut size={18} className="shrink-0" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default PublicHeader;